// src/pages/StaffWallet.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Typography,
  Box,
  IconButton,
  Grid,
  Paper,
} from "@mui/material";
import { Button } from "antd";
import Swal from "sweetalert2";
import { FaTimes } from "react-icons/fa";
import api from "../api"; // <-- নিশ্চিত করো path সঠিক (src/api/index.js)

const StaffWallet = () => {
  const navigate = useNavigate();
  const [storedUser, setStoredUser] = useState(null);
  // data.balance will be numeric (number) to simplify comparisons
  const [data, setData] = useState({
    students: 0,
    teachers: 0,
    staff: 0,
    bill: 0,
    balance: 0,
  });
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [transferAmount, setTransferAmount] = useState("");


  const formatCurrency = (amount) => {
    const val = Number(amount) || 0;
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  useEffect(() => {
    // load user summary from localStorage
    const user = {
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("user_id"),
      name: localStorage.getItem("name"),
      mobile: localStorage.getItem("mobile"),
      role: localStorage.getItem("role"),
      balance: formatCurrency(localStorage.getItem("balance") || "0"),
    };
    setStoredUser(user);

    // guard for token & school_id
    const token = localStorage.getItem("token");
    if (!token) {
      console.warn("StaffWallet: token missing, redirecting to login");
      navigate("/login");
      return;
    }
    const sid = localStorage.getItem("school_id");
    if (!sid) {
      console.warn("StaffWallet: school_id missing, redirecting to login");
      navigate("/login");
      return;
    }

    let aborted = false;
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const id = localStorage.getItem("id") || null;
        // Use axios instance (api) so interceptor adds Authorization & X-School-Id
        const res = await api.post(
          "/get-data",
          { id },
          { signal: controller.signal }
        );

        const result = res?.data || {};
        const resData = result?.data || {};

        if (aborted) return;

        setData({
          students: Number(resData.students || 0),
          teachers: Number(resData.teachers || 0),
          staff: Number(resData.staff || 0),
          bill: parseFloat(resData.bill || 0),
          balance: Number(parseFloat(resData.balance || 0) || 0),
        });

        // update localStorage balance if present
        if (resData.balance !== undefined && resData.balance !== null) {
          localStorage.setItem("balance", String(resData.balance));
        }

        console.info("StaffWallet: fetched data", resData);
      } catch (err) {
        if (err?.name === "AbortError") {
          console.warn("StaffWallet fetch aborted");
          return;
        }
        console.error("StaffWallet fetch error:", err?.response || err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load wallet data";
        setErrorMsg(msg);
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      aborted = true;
      controller.abort();
    };
  }, [navigate]);

  const handleTransfer = async () => {
    const raw = transferAmount;
    const amountToTransfer = parseFloat(raw);
    const availableBalance = Number(data.balance || 0);

    if (!raw || isNaN(amountToTransfer) || amountToTransfer <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Amount",
        text: "Please enter a valid amount.",
        confirmButtonColor: "#1976d2",
      });
      return;
    }

    if (amountToTransfer > availableBalance) {
      Swal.fire({
        icon: "error",
        title: "Insufficient Balance",
        text: "You don't have enough balance to make this transfer.",
        confirmButtonColor: "#d33",
      });
      return;
    }

    try {
      // use api (axios) so interceptor adds Authorization header
      const res = await api.post("/transfer-fund", {
        id: localStorage.getItem("id"),
        amount: amountToTransfer,
      });

      const result = res?.data || {};

      if (result.status) {
        Swal.fire({
          icon: "success",
          title: "Transfer Successful!",
          text: `₹${formatCurrency(amountToTransfer)} has been transferred successfully.`,
          confirmButtonColor: "#4caf50",
        });

        // backend returns new_balance — try to parse
        const newBalanceRaw = result.new_balance ?? result.data?.new_balance ?? null;
        const newBalanceNum = Number(
          (typeof newBalanceRaw === "string" ? newBalanceRaw.replace(/,/g, "") : newBalanceRaw) || 0
        );

        // update localStorage and state consistently
        localStorage.setItem("balance", String(newBalanceNum));

        setData((prev) => ({
          ...prev,
          balance: newBalanceNum,
        }));

        setStoredUser((prev) => ({
          ...prev,
          balance: formatCurrency(newBalanceNum),
        }));

        setTransferAmount("");
      } else {
        Swal.fire({
          icon: "error",
          title: "Transfer Failed",
          text: result.message || "Something went wrong.",
          confirmButtonColor: "#d33",
        });
      }
    } catch (error) {
      console.error("Transfer error:", error?.response || error);
      const msg = error?.response?.data?.message || error?.message || "Request failed";
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: msg,
        confirmButtonColor: "#d33",
      });
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Grid container justifyContent="center" alignItems="center" sx={{ backgroundColor: "#f0f2f5", px: 2, marginTop: "0px" }}>
          <Grid item xs={12} sm={10} md={6} lg={4}>
            <Paper elevation={10} sx={{
              p: 5, borderRadius: 4, textAlign: "center",
              background: "linear-gradient(135deg, #ffffff, #f3f6f9)", boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
              transition: "transform 0.3s ease", '&:hover': { transform: "scale(1.02)" }
            }}>
              <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1a237e" }}>
                💼 Fund Transfer to Staff
              </Typography>

              <Typography variant="h6" sx={{
                fontWeight: 600, color: "#43a047", mb: 3, backgroundColor: "#e8f5e9",
                p: 1.5, borderRadius: 2,
              }}>
                Current Wallet Balance: ₹ {loading ? "..." : formatCurrency(data.balance)}
              </Typography>

              {errorMsg && (
                <Typography color="error" sx={{ mb: 2 }}>{errorMsg}</Typography>
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 500, textAlign: "left", mb: 1 }}>
                Amount to Transfer
              </Typography>

              <TextField
                fullWidth
                type="number"
                label="Enter amount"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                variant="outlined"
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    transferAmount && (
                      <IconButton onClick={() => setTransferAmount("")} edge="end" size="small">
                        <FaTimes style={{ color: "#aaa" }} />
                      </IconButton>
                    )
                  ),
                }}
              />

              <Button
                type="primary"
                fullWidth
                size="large"
                onClick={handleTransfer}
                style={{ fontWeight: "bold", paddingInline: 16, borderRadius: 8, height: 45 }}
              >
                Transfer to Admin
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default StaffWallet;
